import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/** Canonical host. Every absolute URL the page declares has to agree on it. */
const SITE_ORIGIN = 'https://www.apprezzoo.it';

/** Id of the JSON-LD tag, so a later page can find and replace its own. */
const JSON_LD_ID = 'app-json-ld';

export interface PageMeta {
  title: string;
  description: string;
  /** Absolute image URL for the social card. Falls back to the site default. */
  image?: string;
  /** Absolute page URL. Defaults to the current location on this origin. */
  url?: string;
  /** Open Graph type: 'website' for the site, 'article' for content pages. */
  type?: 'website' | 'article';
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  /**
   * The values index.html ships with, captured once before anything overwrites
   * them. Leaving a detail page restores these rather than a second copy of
   * the same strings kept in sync by hand.
   */
  private readonly defaults: PageMeta;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly title: Title,
    private readonly meta: Meta,
  ) {
    this.defaults = {
      title: this.title.getTitle(),
      description: this.meta.getTag('name="description"')?.content ?? '',
      image: this.meta.getTag('property="og:image"')?.content ?? undefined,
      type: 'website',
    };
  }

  setCanonicalURL(url?: string): void {
    try {
      const pathname = this.document?.location?.pathname || '/';
      const canonicalURL = url || `${SITE_ORIGIN}${pathname}`;

      const canonicalLink = this.getOrCreateCanonicalTag();
      if (!canonicalLink) {
        console.warn('[SeoService] Failed to create or retrieve canonical link element.');
        return;
      }

      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalURL);
    } catch (err) {
      console.error('[SeoService] Error setting canonical URL:', err);
    }
  }

  /**
   * Sets the title, the description and the social cards for one page.
   *
   * Without this every route served the title and description of the home
   * page, so a search result for a recipe read "confronta i prezzi del
   * supermercato" and sharing one showed the site's own preview card.
   */
  setPageMeta(page: PageMeta): void {
    try {
      const url = page.url || `${SITE_ORIGIN}${this.document?.location?.pathname || '/'}`;
      const image = page.image || this.defaults.image;
      const type = page.type ?? 'article';

      this.title.setTitle(page.title);
      this.meta.updateTag({ name: 'description', content: page.description });

      this.meta.updateTag({ property: 'og:type', content: type });
      this.meta.updateTag({ property: 'og:url', content: url });
      this.meta.updateTag({ property: 'og:title', content: page.title });
      this.meta.updateTag({ property: 'og:description', content: page.description });

      this.meta.updateTag({ name: 'twitter:url', content: url });
      this.meta.updateTag({ name: 'twitter:title', content: page.title });
      this.meta.updateTag({ name: 'twitter:description', content: page.description });

      if (image) {
        this.meta.updateTag({ property: 'og:image', content: image });
        this.meta.updateTag({ name: 'twitter:image', content: image });
      }
    } catch (err) {
      console.error('[SeoService] Error setting page meta:', err);
    }
  }

  /** Puts back what index.html declares, on leaving a page that changed it. */
  resetPageMeta(): void {
    this.setPageMeta({ ...this.defaults, url: SITE_ORIGIN + '/', type: 'website' });
  }

  /**
   * Publishes a structured data block. One per page: the tag is reused, so a
   * navigation cannot leave the previous page's data behind.
   */
  setJsonLd(data: Record<string, unknown>): void {
    try {
      let script = this.document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
      if (!script) {
        script = this.document.createElement('script');
        script.id = JSON_LD_ID;
        script.type = 'application/ld+json';
        this.document.head?.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    } catch (err) {
      console.error('[SeoService] Error setting JSON-LD:', err);
    }
  }

  clearJsonLd(): void {
    try {
      this.document.getElementById(JSON_LD_ID)?.remove();
    } catch (err) {
      console.error('[SeoService] Error clearing JSON-LD:', err);
    }
  }

  private getOrCreateCanonicalTag(): HTMLLinkElement | null {
    try {
      let link: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
      if (!link) {
        link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        this.document.head?.appendChild(link);
      }
      return link;
    } catch (err) {
      console.error('[SeoService] Error managing canonical tag:', err);
      return null;
    }
  }
}
